import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import api from '../services/api';

interface OrderingApprovalTabProps {
  onError?: (msg: string) => void;
}

const OrderingApprovalTab: React.FC<OrderingApprovalTabProps> = ({ onError }) => {
  const [ttl, setTtl] = useState('14');
  const [portalBase, setPortalBase] = useState('https://order.sonicnet.net.au');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.adminGetApprovalLinkSettings();
        if (!cancelled) {
          setTtl(String(d.ttl_days ?? 14));
          setPortalBase((d.customer_portal_base_url || 'https://order.sonicnet.net.au').replace(/\/$/, ''));
        }
      } catch (e: unknown) {
        onError?.((e as Error)?.message || 'Failed to load approval link settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  const save = async () => {
    setSaving(true);
    onError?.('');
    try {
      const n = parseInt(ttl, 10);
      if (!Number.isFinite(n) || n < 1 || n > 90) {
        onError?.('TTL must be between 1 and 90 days.');
        return;
      }
      const base = portalBase.trim() || 'https://order.sonicnet.net.au';
      await api.adminPutApprovalLinkSettings({ ttl_days: n, customer_portal_base_url: base });
    } catch (e: unknown) {
      onError?.((e as any)?.response?.data?.detail || (e as Error)?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h6" gutterBottom>
        Customer order approval links
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When staff generate an approval link from Order Management, the customer opens it without logging in. The
        full URL shown to customers uses the portal base below (not this SonOps site). Links expire after the number
        of days below; minting a new link replaces the previous one.
      </Typography>
      {loading ? (
        <Typography variant="body2">Loading…</Typography>
      ) : (
        <>
          <TextField
            fullWidth
            size="small"
            label="Customer portal base URL"
            value={portalBase}
            onChange={(e) => setPortalBase(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Origin only, no path — e.g. https://order.sonicnet.net.au (used for review links and approval emails)."
          />
          <TextField
            fullWidth
            size="small"
            label="Link lifetime (days)"
            type="number"
            inputProps={{ min: 1, max: 90 }}
            value={ttl}
            onChange={(e) => setTtl(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default OrderingApprovalTab;
